import IcoMoon from '@/components/IcoMoon'
import Layout, { Stack } from '@/components/Layout'
import LottieImage, { LottieImageHandlers } from '@/components/LottieImage'
import MenuLink from '@/components/MenuLinks'
import { rbp } from '@/constants/measured-scope'
import { useAuth } from '@/context/auth'
import { useFeatureToggle } from '@/context/featureToggles'
import { BottomNavigationActiveTab, useNavigation } from '@/context/navigation'
import { PrimaryNavigation as PrimaryNavigationType, UserMenuNavigation } from '@/shared/api-generated-types'
import AuthMenu from '@/widgets/AuthMenu'
import BottomNavigation from '@/widgets/BottomNavigation'
import SearchMenu from '@/widgets/SearchMenu'
import SliderMenu from '@/widgets/SliderMenu'
import UserMenu from '@/widgets/UserMenu'
import { useMediaQuery } from '@react-hookz/web'
import classNames from 'classnames/bind'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { gql, useQuery } from 'urql'
import * as styles from './style'

const cx = classNames.bind(styles)

type ToggleMenus = 'nav' | 'search' | 'custom' | ''

const QUERY_NAVIGATION = gql`
  query navigation($partnerSlug: String) {
    primaryNavigation(partnerSlug: $partnerSlug) {
      items {
        id
        url
        label
        openInNewTab
        path
        hideForAuthenticated
      }
    }
    userMenuNavigation(partnerSlug: $partnerSlug) {
      items {
        id
        url
        label
        openInNewTab
        path
        hideForAuthenticated
      }
    }
  }
`

export interface Props {
  bottomNavigationActiveTab: BottomNavigationActiveTab
}

const PrimaryNavigation = ({ bottomNavigationActiveTab }: Props): JSX.Element => {
  const aiSearchBoxEnabled = useFeatureToggle('ai_search_box')
  const router = useRouter()
  const {
    state: { user },
  } = useAuth()
  const {
    state: { searchBox },
    dispatch,
  } = useNavigation()
  const lottieRef = useRef<LottieImageHandlers>(null)
  const isLargeDevice = useMediaQuery(`only screen and (min-width : ${rbp.lg})`, { initializeWithValue: false })
  const [menuVisible, setMenuVisible] = useState<ToggleMenus>('')
  const wrapperClasses = cx({
    wrapper: true,
  })

  // Fetch primary navigation from the backend
  const partnerSlug = router.route === '/partner/[slug]' ? router.query?.slug : undefined
  const [{ data, fetching }] = useQuery<{
    primaryNavigation: PrimaryNavigationType
    userMenuNavigation: UserMenuNavigation
  }>({
    query: QUERY_NAVIGATION,
    requestPolicy: 'cache-first',
    variables: { partnerSlug },
  })

  const isAuthenticated = useMemo(() => {
    if (user) {
      return true
    }
    return false
  }, [user])

  useEffect(() => {
    if (!searchBox.open && menuVisible === 'search') setMenuVisible('')
  }, [searchBox.open])

  function hideSearchMenu(): void {
    if (searchBox.open) {
      lottieRef.current?.playReverse()
      setMenuVisible('')
      dispatch({ type: 'SET_SEARCH_OPEN', payload: false })
    }
  }

  function toggleSearchMenu(): void {
    if (searchBox.open) {
      lottieRef.current?.playReverse()
      setMenuVisible('')
      dispatch({ type: 'SET_SEARCH_OPEN', payload: false })
    } else {
      lottieRef.current?.playForward()
      setMenuVisible('search')
      dispatch({ type: 'SET_SEARCH_OPEN', payload: true })
    }
  }

  function resetMenu(): void {
    lottieRef.current?.playReverse()
    setMenuVisible('')
    dispatch({ type: 'SET_SEARCH_OPEN', payload: false })
  }

  return isLargeDevice ? (
    <div className={wrapperClasses}>
      <Layout className={styles.headerContent}>
        <Stack x alignItems="center" justifyContent="space-between">
          <Link href="/" className={styles.logo} prefetch={false} onClick={hideSearchMenu}>
            <img src="/images/logo-large.svg" alt="GetSetUp logo" loading="lazy" />
          </Link>
          <Stack className={styles.navMenu} x alignItems="center" justifyContent="flex-end">
            {fetching ? (
              <></>
            ) : (
              data?.primaryNavigation?.items.map((link, i) =>
                link.hideForAuthenticated && isAuthenticated ? undefined : (
                  <MenuLink
                    key={`menu-link-external-${i}`}
                    label={link.label}
                    url={link.url}
                    path={link.path}
                    openInNewTab={link.openInNewTab}
                  />
                ),
              )
            )}

            {!isAuthenticated && <AuthMenu />}
            {isAuthenticated && (
              <UserMenu
                firstName={user?.firstName}
                fullName={user?.fullName}
                userMenuLinks={data?.userMenuNavigation.items}
              />
            )}
          </Stack>
        </Stack>
      </Layout>
    </div>
  ) : (
    <div className={wrapperClasses}>
      <Layout className={styles.headerContent}>
        <Stack className={styles.topBar} x alignItems="center" justifyContent="space-between">
          <span className={styles.menuButton} onClick={() => setMenuVisible('nav')}>
            <IcoMoon icon="menu" size={24} />
          </span>
          <Link href="/" className={styles.logo} prefetch={false}>
            <img src="/images/logo-touch.svg" alt="GetSetUp logo" loading="lazy" />
          </Link>
          {!aiSearchBoxEnabled || !router.asPath.includes('/helen') ? (
            <span className={styles.menuButton} onClick={toggleSearchMenu}>
              <LottieImage
                autoplay={false}
                className={styles.searchIcon}
                name="search-close-animation"
                path={'https://ddq2bomue87t4.cloudfront.net/lottie/search-close/search-close.json'}
                ref={lottieRef}
              />
            </span>
          ) : (
            <span className={styles.menuButton}></span>
          )}
        </Stack>
      </Layout>
      <SearchMenu menuVisible={menuVisible === 'search'} />
      <SliderMenu
        close={resetMenu}
        menuVisible={menuVisible === 'nav'}
        additionalLinks={data?.primaryNavigation.items}
        userMenuLinks={data?.userMenuNavigation.items}
      />
      <BottomNavigation activeTab={bottomNavigationActiveTab} />
    </div>
  )
}
export default PrimaryNavigation
